/**
 * AdminAdminsScreen — gestionar la lista de administradores Nilabs.
 *
 * Solo visible para CUALQUIER admin (lectura), pero las acciones de
 * crear/modificar/eliminar requieren ser super-admin (el admin de menor id,
 * que actualmente es darknilton). Para el resto, los botones aparecen
 * deshabilitados.
 *
 * Flujos:
 *  - Lista todos los admins con: email, nombre, activo/inactivo, métodos de
 *    login (Google y/o password), última actividad, badge "super" si aplica.
 *  - "Nuevo admin": modal con email + password + nombre → crea o promueve si
 *    el email ya existe.
 *  - Toggle activar/desactivar (solo super, no podés desactivarte a vos mismo).
 *  - Reset password.
 *  - Revocar admin (quita el flag is_admin, no borra el user).
 */
import { useEffect, useMemo, useState } from "react";
import {
  createAdmin,
  listAdmins,
  patchAdmin,
  revokeAdmin,
  type AdminAccount,
  type AdminCreatePayload,
  type AdminPatchPayload,
} from "../../api/admin";
import { useAuth } from "../../contexts/AuthContext";
import { Icon } from "../../lib/Icon";
import { EmptyState } from "../../lib/components";
import { useToast } from "../../lib/toast";
import { AdminBadge, AdminGate } from "./_shared";

export function AdminAdminsScreen() {
  return (
    <AdminGate>
      <Content />
    </AdminGate>
  );
}

function Content() {
  const { user } = useAuth();
  const toast = useToast();
  const [admins, setAdmins] = useState<AdminAccount[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [editing, setEditing] = useState<AdminAccount | "new" | null>(null);

  const load = async () => {
    setLoading(true);
    setErr(null);
    try {
      setAdmins(await listAdmins());
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const meIsSuper = useMemo(() => {
    if (!admins || !user) return false;
    const a = admins.find((x) => x.id === user.id);
    return Boolean(a?.is_super);
  }, [admins, user]);

  const onToggleActive = async (a: AdminAccount) => {
    if (!meIsSuper) {
      toast("Solo el super-admin puede modificar admins");
      return;
    }
    if (a.id === user?.id && !a.is_active === false) {
      // edge case — no permitir desactivarte a vos mismo
    }
    try {
      const updated = await patchAdmin(a.id, { is_active: !a.is_active });
      setAdmins((prev) => prev?.map((x) => (x.id === a.id ? updated : x)) ?? null);
      toast(`${updated.email} ${updated.is_active ? "activado" : "desactivado"}`);
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e));
    }
  };

  const onRevoke = async (a: AdminAccount) => {
    if (!meIsSuper) {
      toast("Solo el super-admin puede revocar admins");
      return;
    }
    if (a.is_super) {
      toast("No podés revocar al super-admin");
      return;
    }
    if (!confirm(`¿Revocar admin de "${a.email}"? El usuario seguirá existiendo (si era entrenador, sigue siéndolo).`)) return;
    try {
      await revokeAdmin(a.id);
      toast(`"${a.email}" ya no es admin`);
      load();
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e));
    }
  };

  const handleSave = async (payload: AdminCreatePayload | AdminPatchPayload, id?: number): Promise<boolean> => {
    try {
      if (id == null) {
        const created = await createAdmin(payload as AdminCreatePayload);
        toast(`Admin "${created.email}" creado`);
      } else {
        const updated = await patchAdmin(id, payload as AdminPatchPayload);
        toast(`"${updated.email}" actualizado`);
      }
      setEditing(null);
      await load();
      return true;
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e));
      return false;
    }
  };

  return (
    <div className="page al-admin">
      <header className="al-admin-header">
        <div>
          <h1>
            Administradores <AdminBadge />
          </h1>
          <p className="muted">
            Personal de Nilabs con acceso al panel de soporte. {meIsSuper
              ? "Como super-admin, podés dar de alta y administrar a los demás."
              : "Solo el super-admin puede crear/modificar admins."}
          </p>
        </div>
        {meIsSuper && (
          <button className="btn btn-primary" onClick={() => setEditing("new")}>
            <Icon name="plus" size={14} /> Nuevo admin
          </button>
        )}
      </header>

      {err && (
        <EmptyState
          icon="x"
          title="Error al cargar"
          body={err}
          action={<button className="btn btn-secondary" onClick={load}>Reintentar</button>}
        />
      )}

      {!err && loading && !admins && (
        <div className="muted" style={{ padding: 24 }}>Cargando admins…</div>
      )}

      {!err && admins && admins.length === 0 && (
        <EmptyState
          icon="user"
          title="Sin admins"
          body="Algo raro pasa — debería haber al menos uno."
        />
      )}

      {!err && admins && admins.length > 0 && (
        <div className="al-admin-admins-grid">
          {admins.map((a) => (
            <AdminRow
              key={a.id}
              admin={a}
              isMe={a.id === user?.id}
              canManage={meIsSuper}
              onEdit={() => setEditing(a)}
              onToggleActive={() => onToggleActive(a)}
              onRevoke={() => onRevoke(a)}
            />
          ))}
        </div>
      )}

      {editing && (
        <AdminForm
          admin={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSave={handleSave}
        />
      )}
    </div>
  );
}

// ----------------------------------------------------------------------
// AdminRow — card del admin con badges + acciones
// ----------------------------------------------------------------------

interface AdminRowProps {
  admin: AdminAccount;
  isMe: boolean;
  canManage: boolean;
  onEdit: () => void;
  onToggleActive: () => void;
  onRevoke: () => void;
}

function AdminRow({ admin, isMe, canManage, onEdit, onToggleActive, onRevoke }: AdminRowProps) {
  const lastSeen = admin.last_seen_at ? new Date(admin.last_seen_at) : null;
  return (
    <div className={"al-admin-admin-card" + (admin.is_active ? "" : " is-inactive")}>
      <div className="al-admin-admin-hd">
        <div className="al-admin-admin-bubble">
          {(admin.name || admin.email).slice(0, 2).toUpperCase()}
        </div>
        <div className="al-admin-admin-titles">
          <div className="al-admin-admin-name">
            {admin.name || admin.email.split("@")[0]}
            {admin.is_super && <span className="al-admin-admin-super">SUPER</span>}
            {isMe && <span className="al-admin-admin-me">VOS</span>}
          </div>
          <div className="tiny muted">{admin.email}</div>
        </div>
      </div>

      <div className="al-admin-admin-meta">
        <span className={"al-admin-admin-status" + (admin.is_active ? " is-on" : " is-off")}>
          {admin.is_active ? "Activo" : "Deshabilitado"}
        </span>
        {admin.has_google && (
          <span className="al-admin-admin-tag" title="Puede entrar con Google">
            <Icon name="check" size={10} /> Google
          </span>
        )}
        {admin.has_password && (
          <span className="al-admin-admin-tag" title="Puede entrar con email/password">
            <Icon name="check" size={10} /> Contraseña
          </span>
        )}
      </div>

      <div className="al-admin-admin-foot">
        <div className="tiny muted">
          {lastSeen ? `Última actividad ${lastSeen.toLocaleDateString("es-CO")}` : "—"}
        </div>
        <div className="al-admin-admin-actions">
          <button
            className="btn btn-ghost btn-sm"
            onClick={onEdit}
            disabled={!canManage}
            title={canManage ? "Editar admin" : "Solo el super-admin puede editar"}
          >
            <Icon name="settings" size={12} /> Editar
          </button>
          <button
            className="btn btn-ghost btn-sm"
            onClick={onToggleActive}
            disabled={!canManage || isMe || admin.is_super}
            title={
              !canManage ? "Solo el super-admin puede modificar"
              : isMe ? "No podés desactivarte a vos mismo"
              : admin.is_super ? "No se puede desactivar al super-admin"
              : (admin.is_active ? "Deshabilitar" : "Activar")
            }
          >
            {admin.is_active ? "Deshabilitar" : "Activar"}
          </button>
          {!admin.is_super && (
            <button
              className="btn btn-ghost btn-sm al-admin-admin-revoke"
              onClick={onRevoke}
              disabled={!canManage || isMe}
              title={isMe ? "No podés revocarte a vos mismo" : "Quitar permisos de admin"}
            >
              <Icon name="x" size={12} /> Revocar
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------
// AdminForm — modal para crear admin (email/pwd) o editar uno existente
// ----------------------------------------------------------------------

interface AdminFormProps {
  admin: AdminAccount | null;
  onClose: () => void;
  onSave: (payload: AdminCreatePayload | AdminPatchPayload, id?: number) => Promise<boolean>;
}

function AdminForm({ admin, onClose, onSave }: AdminFormProps) {
  const isNew = !admin;
  const [email, setEmail] = useState(admin?.email || "");
  const [name, setName] = useState(admin?.name || "");
  const [password, setPassword] = useState("");
  const [resetPassword, setResetPassword] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, busy]);

  const canSave = isNew
    ? email.trim().length > 0 && password.length >= 8
    : true;   // editar: nombre puede estar vacío, password solo si reset

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSave || busy) return;
    setBusy(true);
    try {
      if (isNew) {
        const ok = await onSave({
          email: email.trim().toLowerCase(),
          password,
          name: name.trim() || null,
        });
        if (!ok) setBusy(false);
      } else {
        const patch: AdminPatchPayload = {};
        if (name.trim() !== (admin.name || "")) patch.name = name.trim() || null;
        if (resetPassword && password.length >= 8) patch.new_password = password;
        const ok = await onSave(patch, admin.id);
        if (!ok) setBusy(false);
      }
    } finally {
      // si onSave devuelve false (error) ya hicimos setBusy(false) arriba
    }
  };

  return (
    <div className="al-modal-backdrop" onClick={() => !busy && onClose()}>
      <div
        className="al-modal al-admin-admin-form"
        role="dialog"
        aria-label="Admin"
        onClick={(ev) => ev.stopPropagation()}
      >
        <div className="al-modal-hd">
          <div>
            <h3 style={{ margin: 0, fontFamily: "var(--font-display)", fontSize: 18 }}>
              {isNew ? "Nuevo administrador" : `Editar admin`}
            </h3>
            <div className="tiny muted" style={{ marginTop: 2 }}>
              {isNew
                ? "Si el email ya existe como entrenador, lo promovemos a admin sin perder sus datos."
                : admin.email}
            </div>
          </div>
          <button className="icon-btn" onClick={onClose} title="Cerrar" disabled={busy}>
            <Icon name="x" size={16} />
          </button>
        </div>

        <form onSubmit={submit} className="al-admin-admin-form-body">
          <label className="al-food-custom-field">
            <span className="tiny" style={{ fontWeight: 600 }}>Email *</span>
            <input
              type="email"
              className="al-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@nilabs.co"
              disabled={!isNew || busy}
              required
              autoFocus={isNew}
            />
          </label>

          <label className="al-food-custom-field">
            <span className="tiny" style={{ fontWeight: 600 }}>Nombre completo</span>
            <input
              type="text"
              className="al-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ana Pérez"
              disabled={busy}
            />
          </label>

          {isNew ? (
            <label className="al-food-custom-field">
              <span className="tiny" style={{ fontWeight: 600 }}>Contraseña inicial *</span>
              <input
                type="password"
                className="al-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="mínimo 8 caracteres"
                disabled={busy}
                required
                minLength={8}
              />
              <span className="tiny muted" style={{ marginTop: 4 }}>
                Pasásela por canal seguro. El admin la podrá cambiar después.
              </span>
            </label>
          ) : (
            <>
              <label className="al-admin-admin-form-toggle">
                <input
                  type="checkbox"
                  checked={resetPassword}
                  onChange={(e) => setResetPassword(e.target.checked)}
                  disabled={busy}
                />
                <span>Resetear contraseña</span>
              </label>
              {resetPassword && (
                <label className="al-food-custom-field">
                  <span className="tiny" style={{ fontWeight: 600 }}>Nueva contraseña</span>
                  <input
                    type="password"
                    className="al-input"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="mínimo 8 caracteres"
                    disabled={busy}
                    minLength={8}
                  />
                </label>
              )}
            </>
          )}

          <div className="al-admin-admin-form-foot">
            <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
              Cancelar
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={!canSave || busy || (resetPassword && password.length < 8)}
            >
              {busy ? "Guardando…" : (isNew ? "Crear admin" : "Guardar cambios")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
